import React from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { PlotlyTrace } from "api-client";
import { tooltipModuleList } from "features/Dashboards/Base/DashboardSimpleNumber";
import useDashboards from "features/Dashboards/useDashboards";
import { buildingDashboardStore } from "store/Building/BuildingDashboardStore";
import { buildingStore } from "store/Building/BuildingStore";
import ModuleBarBlock from "./ModuleBarBlock";

/**
 * X-axis range for a stacked (relative) horizontal bar: in relative barmode
 * positives stack up from 0 and negatives down from 0, so the extent is the
 * largest positive stack and the smallest negative stack — not the max of any
 * single point (what the shared store getter uses).
 */
function stackedXRange(traces: Array<PlotlyTrace>): number[] {
  const values = traces.map((t) =>
    (Array.isArray(t.y) ? t.y : []).map((v) => Number(v) || 0)
  );
  const categories = Math.max(0, ...values.map((a) => a.length));
  let posMax = 0;
  let negMin = 0;
  for (let i = 0; i < categories; i++) {
    let pos = 0;
    let neg = 0;
    for (const arr of values) {
      const v = arr[i] ?? 0;
      if (v >= 0) pos += v;
      else neg += v;
    }
    posMax = Math.max(posMax, pos);
    negMin = Math.min(negMin, neg);
  }
  // Pad both ends so the 0-line (bar baseline) sits inside the plot area rather
  // than on the axis edge — otherwise Plotly clips it at some zoom levels.
  const pad = (posMax - negMin) * 0.02 || 1;
  return [negMin - pad, posMax + pad];
}

export default observer(function GwpModuleBreakdowns() {
  const { t } = useTranslation();
  const { showEmptyState } = useDashboards();
  const info = buildingDashboardStore.emissionsInfo;

  const showB6 =
    buildingDashboardStore.hasB6Report &&
    !buildingStore.buildingHasKeineCertification();

  // Construction block is A–C only; module D is a separate credit (shown in the
  // top metrics), so exclude its trace from this breakdown.
  const constructionTraces = (info?.gwp_traces ?? []).filter(
    (trace) => (trace.name ?? "").trim().toUpperCase() !== "D"
  );

  return (
    <div className="grid grid-cols-2 gap-4 mb-5">
      <ModuleBarBlock
        title={t("levels.grayConstruction")}
        total={info?.gwp_total}
        traces={constructionTraces}
        moduleListTooltip={tooltipModuleList(t, undefined, info?.gwp_modules)}
        plotXRange={stackedXRange(constructionTraces)}
      />
      {showB6 ? (
        <ModuleBarBlock
          title={t("levels.operationalB6")}
          total={info?.operational_energy_total}
          traces={info?.operational_energy_traces ?? []}
          moduleListTooltip={tooltipModuleList(
            t,
            undefined,
            info?.operational_energy_modules
          )}
          autorange
        />
      ) : (
        <div className="rounded-lg bg-gray-100 p-5 flex flex-col h-full">
          <h3 className="text-xs font-bold uppercase text-gray-400">
            {t("levels.operationalB6")}
          </h3>
          <div className="flex-1 flex items-center justify-center">
            {showEmptyState("w-11 h-11 mt-11")}
          </div>
        </div>
      )}
    </div>
  );
});
